import { useContext, useState } from "react";
import { retrieveValue, storeValue } from "@/utility/storage";
import { SessionContext } from "@/utility/useSession";

export type BookingsPreferenceName = "liveUpdates";

export const getBookingsPreferenceKey = (
  name: BookingsPreferenceName,
  userUuid: string,
) => `bookings.v2.${name}.${userUuid}`;

const readPreference = (key: string | null, defaultValue: boolean) => {
  if (key == null) return defaultValue;
  return retrieveValue(key, defaultValue ? "1" : "0") === "1";
};

/**
 * A boolean Bookings page setting persisted in local storage. 
 * Keyed by the logged-in user.
 */
export const useBookingsPreference = (
  name: BookingsPreferenceName,
  defaultValue: boolean,
) => {
  const { current: session } = useContext(SessionContext);
  const userUuid = session?.site?.user?.uuid ?? null;
  const key =
    userUuid == null ? null : getBookingsPreferenceKey(name, userUuid);
  const [state, setState] = useState(() => ({
    key,
    value: readPreference(key, defaultValue),
  }));
  const value =
    state.key === key ? state.value : readPreference(key, defaultValue);

  const setValue = (nextValue: boolean) => {
    if (key != null) {
      storeValue(key, nextValue ? "1" : "0");
    }
    setState({ key, value: nextValue });
  };

  return [value, setValue] as const;
};
